import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FileCheck, ChevronDown, Search, Loader2, Plus, RefreshCw, Upload, CheckCircle, AlertCircle, Trash2 } from 'lucide-react';
import TermsCheckerCard from './TermsCheckerCard';
import TermsComparisonModal from './TermsComparisonModal';
import BrandProcessingPopup from './BrandProcessingPopup';
import {
  startBrandProcessing,
  completeBrandProcessing,
  removeBrandProcessing,
  selectProcessingBrands,
} from '../../store/slices/termsCheckerSlice';

// Country options
const COUNTRY_OPTIONS = [
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'CA', name: 'Canada' },
  { code: 'AU', name: 'Australia' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'ES', name: 'Spain' },
  { code: 'IT', name: 'Italy' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'SE', name: 'Sweden' },
  { code: 'NO', name: 'Norway' },
  { code: 'DK', name: 'Denmark' },
  { code: 'FI', name: 'Finland' },
  { code: 'IE', name: 'Ireland' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'IN', name: 'India' },
  { code: 'SG', name: 'Singapore' },
  { code: 'JP', name: 'Japan' },
  { code: 'KR', name: 'South Korea' },
  { code: 'BR', name: 'Brazil' },
  { code: 'MX', name: 'Mexico' },
  { code: 'AR', name: 'Argentina' },
  { code: 'ZA', name: 'South Africa' },
];

// Vertical options
const VERTICAL_OPTIONS = [
  { value: 'casino', name: 'Casino' },
  { value: 'sports', name: 'Sports' },
  { value: 'poker', name: 'Poker' },
  { value: 'bingo', name: 'Bingo' },
  { value: 'lottery', name: 'Lottery' },
  { value: 'esports', name: 'Esports' },
];

// Regulatory market options
const REGULATORY_OPTIONS = [
  { value: 'UK', name: 'UK (UKGC)' },
  { value: 'Malta', name: 'Malta (MGA)' },
];

// Searchable dropdown component
const SearchableSelectFilter = ({ label, options, selectedValue, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const selectedOption = options.find(option => option.value === selectedValue);

  const filteredOptions = options.filter(option =>
    option.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (value) => {
    onChange(value);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white cursor-pointer text-left flex items-center gap-2"
        >
          {selectedOption ? (
            <>
              {selectedOption.icon && (
                <img
                  src={selectedOption.icon}
                  alt=""
                  className="w-4 h-4 rounded"
                  onError={(e) => e.target.style.display = 'none'}
                />
              )}
              <span className="truncate">{selectedOption.name}</span>
            </>
          ) : selectedValue && !selectedOption ? (
            <span className="truncate text-gray-800">{selectedValue}</span>
          ) : (
            <span className="text-gray-500">{placeholder || label}</span>
          )}

          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <ChevronDown
              className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
                isOpen ? 'rotate-180' : 'rotate-0'
              }`}
            />
          </div>
        </button>

        {isOpen && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
            <div className="p-2 border-b border-gray-100">
              <input
                ref={searchInputRef}
                type="text"
                placeholder={`Search ${label.toLowerCase()}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-2 py-1 border border-gray-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div
              onClick={() => handleSelect('')}
              className="px-3 py-2 hover:bg-gray-50 cursor-pointer flex items-center gap-2 text-gray-500"
            >
              {placeholder || label}
            </div>

            {filteredOptions.map((option, index) => (
              <div
                key={index}
                onClick={() => handleSelect(option.value)}
                className="px-3 py-2 hover:bg-gray-50 cursor-pointer flex items-center gap-2 border-t border-gray-100"
              >
                {option.icon && (
                  <img
                    src={option.icon}
                    alt=""
                    className="w-4 h-4 rounded flex-shrink-0"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                )}
                <span className="truncate">{option.name}</span>
              </div>
            ))}

            {filteredOptions.length === 0 && searchTerm && (
              <div className="px-3 py-2 text-gray-500 text-sm">
                No results found
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const TermsChecker = () => {
  const basePath = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';
  const dispatch = useDispatch();
  const processingBrands = useSelector(selectProcessingBrands);

  // Form states
  const [brandName, setBrandName] = useState('');
  const [brandUrl, setBrandUrl] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedVerticals, setSelectedVerticals] = useState([]);
  const [verticalTerms, setVerticalTerms] = useState({});
  const [selectedRegulatory, setSelectedRegulatory] = useState('');
  const [currentOffer, setCurrentOffer] = useState('');
  const [monthlyOffer, setMonthlyOffer] = useState('');

  // Data states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const [projects, setProjects] = useState([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);

  // Processing popup state
  const [showProcessingPopup, setShowProcessingPopup] = useState(false);
  const [processingBrandName, setProcessingBrandName] = useState('');
  const [newBrandId, setNewBrandId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [comparisonModalOpen, setComparisonModalOpen] = useState(false);
  const [selectedProjectForComparison, setSelectedProjectForComparison] = useState(null);

  // CSV Upload states (for V3 API)
  const [csvContent, setCsvContent] = useState('');
  const [uploadedBacklinksCount, setUploadedBacklinksCount] = useState(0);
  const [isUploadingCSV, setIsUploadingCSV] = useState(false);
  const csvFileInputRef = useRef(null);

  // Fetch existing projects on mount
  useEffect(() => {
    fetchProjects();
    fetchUploadedBacklinksCount();
  }, []);

  // Fetch uploaded backlinks count from V3 API
  const fetchUploadedBacklinksCount = async () => {
    try {
      const response = await fetch(`${basePath}/api/terms-checker-v3/uploaded-backlinks`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setUploadedBacklinksCount(data.count || 0);
      }
    } catch (error) {
      console.error('Error fetching uploaded backlinks:', error);
    }
  };

  // Handle CSV file upload
  const handleCSVFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setCsvContent(e.target.result);
      };
      reader.readAsText(file);
    }
  };

  // Upload CSV to V3 API
  const handleUploadCSV = async () => {
    if (!csvContent.trim()) {
      alert('Please enter or upload CSV content first');
      return;
    }

    setIsUploadingCSV(true);
    try {
      const response = await fetch(`${basePath}/api/terms-checker-v3/upload-csv`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ csvContent })
      });

      const data = await response.json();
      if (data.success) {
        setUploadedBacklinksCount(data.count);
        setCsvContent('');
        alert(`Successfully uploaded ${data.count} backlinks`);
      } else {
        alert(data.message || 'Failed to upload CSV');
      }
    } catch (error) {
      console.error('Error uploading CSV:', error);
      alert('Failed to upload CSV');
    } finally {
      setIsUploadingCSV(false);
    }
  };

  // Clear uploaded backlinks
  const handleClearBacklinks = async () => {
    try {
      const response = await fetch(`${basePath}/api/terms-checker-v3/uploaded-backlinks`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setUploadedBacklinksCount(0);
      }
    } catch (error) {
      console.error('Error clearing backlinks:', error);
    }
  };

  const fetchProjects = async () => {
    setIsLoadingProjects(true);
    try {
      const response = await fetch(`${basePath}/api/terms-checker`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setProjects(data.data || []);
      } else {
        console.error('Failed to fetch projects:', data.message);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setIsLoadingProjects(false);
    }
  };

  // Handle vertical toggle
  const handleVerticalToggle = (verticalValue) => {
    setSelectedVerticals(prev => {
      if (prev.includes(verticalValue)) {
        // Remove vertical and its terms
        const newVerticals = prev.filter(v => v !== verticalValue);
        setVerticalTerms(prevTerms => {
          const newTerms = { ...prevTerms };
          delete newTerms[verticalValue];
          return newTerms;
        });
        return newVerticals;
      } else {
        // Add vertical
        return [...prev, verticalValue];
      }
    });
  };

  // Handle vertical terms change
  const handleVerticalTermsChange = (verticalValue, terms) => {
    setVerticalTerms(prev => ({
      ...prev,
      [verticalValue]: terms
    }));
  };

  const handleSubmit = async () => {
    if (!brandName.trim()) {
      alert('Please enter a brand name');
      return;
    }
    if (!brandUrl.trim()) {
      alert('Please enter a brand URL');
      return;
    }

    // Check if CSV content or previously uploaded backlinks exist
    if (!csvContent.trim() && uploadedBacklinksCount === 0) {
      alert('Please enter or upload CSV backlinks (SEMrush is disabled for testing)');
      return;
    }

    setIsSubmitting(true);
    // Show processing popup
    setProcessingBrandName(brandName.trim());
    setShowProcessingPopup(true);

    try {
      // Step 1: Upload CSV if there's new content
      if (csvContent.trim()) {
        const uploadResponse = await fetch(`${basePath}/api/terms-checker-v3/upload-csv`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ csvContent })
        });

        const uploadData = await uploadResponse.json();
        if (!uploadData.success) {
          setShowProcessingPopup(false);
          alert(uploadData.message || 'Failed to upload CSV backlinks');
          setIsSubmitting(false);
          return;
        }
        setUploadedBacklinksCount(uploadData.count);
        setCsvContent('');
      }

      // Step 2: Start processing with V3 API
      const payload = {
        brandName: brandName.trim(),
        brandUrl: brandUrl.trim(),
      };


      const response = await fetch(`${basePath}/api/terms-checker-v3/start`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Store the new brand ID for tracking processing status
        const brandId = data.data?.brandId;
        setNewBrandId(brandId);

        // Start tracking in Redux
        if (brandId) {
          dispatch(startBrandProcessing({ brandId, brandName: brandName.trim() }));
        }

        setResults({
          message: 'Terms check started successfully (V3)',
          data: data
        });
        // Hide form and clear
        setShowForm(false);
        clearForm();
      } else {
        setShowProcessingPopup(false);
        alert(data.message || 'Failed to start terms check');
      }

    } catch (error) {
      console.error('Error submitting terms check:', error);
      setShowProcessingPopup(false);
      alert('Failed to submit terms check. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle processing popup completion
  const handleProcessingComplete = async () => {
    setShowProcessingPopup(false);

    // Mark brand processing as complete in Redux
    if (newBrandId) {
      dispatch(completeBrandProcessing({ brandId: newBrandId }));
      // Remove from processing after a delay
      setTimeout(() => {
        dispatch(removeBrandProcessing({ brandId: newBrandId }));
      }, 5000);
    }

    // Refresh projects to get updated affiliates count
    await fetchProjects();
    setNewBrandId(null);
  };

  const clearForm = () => {
    setBrandName('');
    setBrandUrl('');
    setSelectedCountry('');
    setSelectedVerticals([]);
    setVerticalTerms({});
    setSelectedRegulatory('');
    setCurrentOffer('');
    setMonthlyOffer('');
    setResults(null);
  };

  const isFormValid = () => {
    return brandName.trim() !== '' && brandUrl.trim() !== '';
  };

  const handleProjectClick = (project) => {
    // TODO: Navigate to project details or open modal
  };

  // Handle view affiliates (open comparison modal)
  const handleViewAffiliates = (project) => {
    setSelectedProjectForComparison(project);
    setComparisonModalOpen(true);
  };

  // Handle edit project
  const handleEditProject = (project) => {
    setEditingProject(project);
    setBrandName(project.brand_name || '');
    setBrandUrl(project.brand_url?.replace(/^https?:\/\//, '') || '');
    setSelectedCountry(project.country || '');

    // Parse verticals
    const parsedVerticals = typeof project.verticals === 'string'
      ? JSON.parse(project.verticals || '[]')
      : (project.verticals || []);
    setSelectedVerticals(parsedVerticals);

    // Parse vertical terms
    const parsedTerms = typeof project.vertical_terms === 'string'
      ? JSON.parse(project.vertical_terms || '{}')
      : (project.vertical_terms || {});
    setVerticalTerms(parsedTerms);

    setSelectedRegulatory(project.regulatory_market || '');
    setCurrentOffer(project.current_offer || '');
    setMonthlyOffer(project.monthly_offer || '');
    setShowForm(true);
  };

  // Handle delete project
  const handleDeleteProject = async (project) => {
    if (!window.confirm(`Are you sure you want to delete "${project.brand_name}"? This will also delete all associated affiliates and backlinks data.`)) {
      return;
    }

    setIsDeleting(true);
    try {
      const response = await fetch(`${basePath}/api/terms-checker/${project.id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        await fetchProjects();
      } else {
        alert(data.message || 'Failed to delete project');
      }
    } catch (error) {
      console.error('Error deleting project:', error);
      alert('Failed to delete project. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle update project
  const handleUpdateProject = async () => {
    if (!brandName.trim() || !brandUrl.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        brandName: brandName.trim(),
        brandUrl: brandUrl.trim(),
        country: selectedCountry,
        verticals: selectedVerticals,
        verticalTerms: verticalTerms,
        regulatory: selectedRegulatory,
        currentOffer: currentOffer.trim(),
        monthlyOffer: monthlyOffer.trim(),
      };

      const response = await fetch(`${basePath}/api/terms-checker/${editingProject.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        await fetchProjects();
        setShowForm(false);
        setEditingProject(null);
        clearForm();
      } else {
        alert(data.message || 'Failed to update project');
      }
    } catch (error) {
      console.error('Error updating project:', error);
      alert('Failed to update project. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-4 min-h-screen bg-gray-100">
      {/* Header */}
      <div className="mb-6">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold mb-2 text-gray-800">
                Terms Checker
              </h1>
              <p className="text-gray-600 leading-relaxed max-w-xl">
                Check and validate terms across your brands for different markets and verticals
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={fetchProjects}
                disabled={isLoadingProjects}
                className="p-2 text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                title="Refresh projects"
              >
                <RefreshCw className={`w-5 h-5 ${isLoadingProjects ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={() => setShowForm(!showForm)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Brand
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Form Section - Collapsible */}
      {showForm && (
      <div className="mb-6">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-medium text-gray-800">
                {editingProject ? 'Edit Brand' : 'Add New Brand'}
              </h2>
            </div>

            <button
              onClick={() => { clearForm(); setShowForm(false); setEditingProject(null); }}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Brand Name */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Brand Name
              </label>
              <input
                type="text"
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
                placeholder="Enter brand name"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              />
            </div>

            {/* Brand URL */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Brand URL
              </label>
              <input
                type="url"
                value={brandUrl}
                onChange={(e) => setBrandUrl(e.target.value)}
                placeholder="https://example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              />
            </div>

            {/* Country Selection */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Country
              </label>
              <select
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              >
                <option value="">Select Country</option>
                {COUNTRY_OPTIONS.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Vertical Multi-Select */}
            <div className="relative lg:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Verticals
              </label>
              <div className="flex flex-wrap gap-2">
                {VERTICAL_OPTIONS.map((vertical) => (
                  <button
                    key={vertical.value}
                    type="button"
                    onClick={() => handleVerticalToggle(vertical.value)}
                    className={`px-4 py-2 rounded-lg border transition-colors ${
                      selectedVerticals.includes(vertical.value)
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                    }`}
                  >
                    {vertical.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Dynamic Textboxes for Selected Verticals */}
            {selectedVerticals.length > 0 && selectedVerticals.map((verticalValue) => {
              const vertical = VERTICAL_OPTIONS.find(v => v.value === verticalValue);
              return (
                <div key={verticalValue} className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {vertical?.name} Terms
                  </label>
                  <textarea
                    value={verticalTerms[verticalValue] || ''}
                    onChange={(e) => handleVerticalTermsChange(verticalValue, e.target.value)}
                    placeholder={`Enter ${vertical?.name.toLowerCase()} terms (comma separated)`}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  />
                </div>
              );
            })}

            {/* Regulatory Market */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Regulatory Market
              </label>
              <select
                value={selectedRegulatory}
                onChange={(e) => setSelectedRegulatory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              >
                <option value="">Select Market</option>
                {REGULATORY_OPTIONS.map((market) => (
                  <option key={market.value} value={market.value}>
                    {market.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* CSV Upload Section */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="text-sm font-medium text-gray-700 mb-4 flex items-center gap-2">
              <Upload className="w-4 h-4" />
              Upload Backlinks CSV (Testing Mode)
            </h3>

            {/* Upload status */}
            <div className="mb-4 p-3 bg-blue-50 rounded-lg flex items-center justify-between">
              <span className="text-blue-800 flex items-center gap-2">
                {uploadedBacklinksCount > 0 ? (
                  <>
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-green-700">{uploadedBacklinksCount} backlinks uploaded</span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-4 h-4 text-yellow-600" />
                    <span className="text-yellow-700">No backlinks uploaded (SEMrush disabled for testing)</span>
                  </>
                )}
              </span>
              {uploadedBacklinksCount > 0 && (
                <button
                  onClick={handleClearBacklinks}
                  className="text-sm text-red-600 hover:text-red-800 flex items-center gap-1"
                >
                  <Trash2 className="w-3 h-3" />
                  Clear
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* File Upload */}
              <div>
                <input
                  type="file"
                  ref={csvFileInputRef}
                  accept=".csv,.txt"
                  onChange={handleCSVFileChange}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => csvFileInputRef.current?.click()}
                  className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors flex items-center justify-center gap-2"
                >
                  <Upload className="w-4 h-4" />
                  Click to upload CSV file
                </button>
              </div>

              {/* Manual Input */}
              <div>
                <textarea
                  value={csvContent}
                  onChange={(e) => setCsvContent(e.target.value)}
                  placeholder="Or paste domains here (one per line):&#10;example.com&#10;anothersite.co.uk"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none font-mono text-sm"
                />
              </div>
            </div>

            <p className="text-xs text-gray-500 mt-4">
              CSV format: one domain per line (e.g., example.com) or with columns: domain,ascore,backlinks,country
            </p>
          </div>

          {/* Offers Section */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="text-sm font-medium text-gray-700 mb-4">Official Offers</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Current Offer */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Offer
                </label>
                <textarea
                  value={currentOffer}
                  onChange={(e) => setCurrentOffer(e.target.value)}
                  placeholder="e.g., Deposit £10 and get 1 Bonus Spin"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
                <p className="text-xs text-gray-500 mt-1">
                  The main offer shown to new players
                </p>
              </div>

              {/* Monthly Offer */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Monthly Offer
                </label>
                <textarea
                  value={monthlyOffer}
                  onChange={(e) => setMonthlyOffer(e.target.value)}
                  placeholder="e.g., 50% reload bonus up to £100"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Recurring monthly promotions
                </p>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex justify-end">
              <button
                onClick={editingProject ? handleUpdateProject : handleSubmit}
                disabled={!isFormValid() || isSubmitting}
                className={`px-6 py-2.5 rounded-lg transition-colors font-medium flex items-center gap-2 ${
                  !isFormValid() || isSubmitting
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {editingProject ? 'Updating...' : 'Checking...'}
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4" />
                    {editingProject ? 'Update Brand' : 'Check Terms'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Projects Grid */}
      <div className="mb-6">
        {isLoadingProjects ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <span className="ml-3 text-gray-600">Loading projects...</span>
          </div>
        ) : projects.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((project) => (
              <TermsCheckerCard
                key={project.id}
                project={project}
                onClick={() => handleProjectClick(project)}
                onEdit={handleEditProject}
                onDelete={handleDeleteProject}
                onViewAffiliates={handleViewAffiliates}
                onReclassify={() => fetchProjects()}
              />
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center">
            <FileCheck className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-700 mb-2">No Projects Yet</h3>
            <p className="text-gray-500 mb-4">Add your first brand to start checking terms compliance</p>
            <button
              onClick={() => setShowForm(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Brand
            </button>
          </div>
        )}
      </div>

      {/* Results Section */}
      {results && (
        <div className="mb-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <h2 className="text-lg font-medium text-gray-800 mb-4">Results</h2>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-green-700">{results.message}</p>
              <pre className="mt-2 text-sm text-gray-600 overflow-x-auto">
                {JSON.stringify(results.data, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      )}

      {/* Info Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <div className="flex gap-3">
          <FileCheck className="w-6 h-6 text-blue-600 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-blue-900 mb-2">About Terms Checker</h3>
            <p className="text-sm text-blue-800">
              The Terms Checker tool allows you to validate and check terms compliance across your brands
              for different markets (UK, Malta) and verticals (Casino, Sports). Enter your casino and sports
              terms to verify they meet regulatory requirements.
            </p>
          </div>
        </div>
      </div>

      {/* Terms Comparison Modal */}
      <TermsComparisonModal
        isOpen={comparisonModalOpen}
        onClose={() => {
          setComparisonModalOpen(false);
          setSelectedProjectForComparison(null);
        }}
        project={selectedProjectForComparison}
      />

      {/* Brand Processing Popup */}
      <BrandProcessingPopup
        isOpen={showProcessingPopup}
        brandName={processingBrandName}
        onComplete={handleProcessingComplete}
      />
    </div>
  );
};

export default TermsChecker;
